import { memo, useCallback, type FC } from "react";
import type { NavigateFunction } from "react-router-dom";

import { AvatarInitials } from "@/components/Avatar";
import { commandItemIconClassCentered } from "@/components/command-palette/constants";
import { CommandEmpty, CommandGroup, CommandItem, CommandLoading } from "@/components/flexkit/Command";
import { getPrincipal, setPrincipal } from "@/principal";
import { useGetAccount } from "@/queries/account-queries";

type OrganizationsPageProps = {
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
};

export const OrganizationsPage: FC<OrganizationsPageProps> = memo(function OrganizationsPage({ runCommand, navigate }) {
    const { data: account, isPending } = useGetAccount();
    const principal = getPrincipal();

    const handleSwitch = useCallback(
        (memberId: string) => {
            runCommand(() => {
                setPrincipal(memberId);
                navigate("/", { replace: true });
            });
        },
        [runCommand, navigate],
    );

    if (isPending) {
        return <CommandLoading>Loading organizations...</CommandLoading>;
    }

    const memberships = account?.memberships ?? [];
    // Exclude the current org — no point switching to where you already are.
    const otherOrgs = memberships.filter((m) => m.userId !== principal);

    if (otherOrgs.length === 0) {
        return <CommandEmpty>No other organizations.</CommandEmpty>;
    }

    return (
        <CommandGroup heading="Organizations">
            {otherOrgs.map((m) => (
                <CommandItem key={m.userId} value={m.organizationName} onSelect={() => handleSwitch(m.userId)}>
                    <span className={commandItemIconClassCentered}>
                        <AvatarInitials name={m.organizationName} size={16} />
                    </span>
                    <span>{m.organizationName}</span>
                </CommandItem>
            ))}
        </CommandGroup>
    );
});
